/* ─────────────────────────────────────────────────────────────
   useDynamicOptions — async option loading for DynamicDataSource fields.

   Fetches a list of {value, label} options from a registered GraphQL
   operation, resolving $context.* variable placeholders from runtimeContext.
   Results are cached per (operation, resolvedVariables) via TanStack Query.
   ───────────────────────────────────────────────────────────── */

import { useQuery } from '@tanstack/react-query';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { graphqlClient } from '@/lib/graphql_client';
import {
  ShareClassesByEntityDocument,
  AppointmentsByEntityDocument,
  SharePlansByEntityDocument,
  DebentureCategoriesByEntityDocument,
  DebentureHoldersByEntityDocument,
  DebentureCertificatesByEntityDocument,
  LegalEntitiesDocument,
  NaturalPersonsDocument,
  ShareholdingsByEntityDocument,
  RelationshipsByParentDocument,
} from '@/gql/graphql';
import type { DynamicDataSource, RuntimeContext } from '../config/types';

/* ── Public types ── */

export interface DynamicOption {
  value: string;
  label: string;
}

export interface UseDynamicOptionsResult {
  options: DynamicOption[];
  isLoading: boolean;
  isError: boolean;
}

/* ── Variable resolution ── */

/**
 * Variables that are OPTIONAL on every backing operation (declared `: ID` / nullable,
 * defaulting to the caller's own tenant when omitted). Unlike required args, an
 * unresolved optional must NOT disable the query — we simply drop it and let the
 * server fall back. This lets owner-scoped pickers (`actingTenantId`) improve where
 * the host provides the owner tenant, without regressing hosts that don't.
 */
const OPTIONAL_VARIABLES = new Set<string>(['actingTenantId']);

function isUnresolved(v: string): boolean {
  return !v || v.startsWith('$context.');
}

/**
 * Resolves `$context.*` placeholders in a variables map using runtimeContext.
 *
 * @example
 * resolveVariables({ legalEntityId: '$context.entityId' }, { entityId: 'abc' })
 * // → { legalEntityId: 'abc' }
 */
export function resolveVariables(
  variables: Record<string, string>,
  runtimeContext: RuntimeContext,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(variables).map(([key, val]) => {
      if (typeof val === 'string' && val.startsWith('$context.')) {
        const contextKey = val.slice('$context.'.length);
        const resolved = runtimeContext[contextKey];
        return [key, typeof resolved === 'string' ? resolved : val];
      }
      return [key, val];
    }),
  );
}

/* ── Operation registry ── */

// Explicit registry — do NOT use dynamic imports or computed keys.
// This keeps the registry tree-shakeable and prevents accidental additions.
//
// To register a new operation:
//   1. Add a .graphql file in src/graphql/operations/ with the query
//   2. Run `pnpm run codegen` to generate the TypedDocumentNode
//   3. Import the document constant below and add to OPERATION_REGISTRY
//
// Operations blocked on BE:
//   PartiesByEntity — T04 (needs BE to ship partiesByEntity query)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDocument = TypedDocumentNode<any, any>;

const OPERATION_REGISTRY: Record<string, AnyDocument> = {
  ShareClassesByEntity: ShareClassesByEntityDocument,
  AppointmentsByEntity: AppointmentsByEntityDocument,
  SharePlansByEntity: SharePlansByEntityDocument,
  DebentureCategoriesByEntity: DebentureCategoriesByEntityDocument,
  DebentureHoldersByEntity: DebentureHoldersByEntityDocument,
  DebentureCertificatesByEntity: DebentureCertificatesByEntityDocument,
  LegalEntities: LegalEntitiesDocument,
  NaturalPersons: NaturalPersonsDocument,
  ShareholdingsByEntity: ShareholdingsByEntityDocument,
  RelationshipsByParent: RelationshipsByParentDocument,
};

function getOperationDocument(operation: string): AnyDocument {
  const doc = OPERATION_REGISTRY[operation];
  if (!doc) {
    throw new Error(
      `useDynamicOptions: unknown operation "${operation}". ` +
        `Register it in OPERATION_REGISTRY in useDynamicOptions.ts.`,
    );
  }
  return doc;
}

/* ── Hook ── */

/**
 * Fetches options for a `dynamic` dataSource field.
 *
 * - Resolves `$context.*` variable placeholders from runtimeContext
 * - Disabled (no query fired) when any resolved variable is falsy
 * - Caches per ['dynamicOptions', operation, resolvedVars] (staleTime: 5 min)
 *
 * @example
 * const { options, isLoading } = useDynamicOptions(
 *   { type: 'dynamic', operation: 'ShareClassesByEntity',
 *     variables: { legalEntityId: '$context.entityId' },
 *     mapResponse: { path: 'shareClassesByEntity', value: 'id', label: 'class_name' } },
 *   { entityId: 'abc-123' }
 * );
 */
export function useDynamicOptions(
  dataSource: DynamicDataSource,
  runtimeContext: RuntimeContext,
): UseDynamicOptionsResult {
  const resolvedVars = resolveVariables(dataSource.variables, runtimeContext);

  // Partition resolved variables into the request payload + a required-resolution gate:
  // 1. operation must be non-empty (guards the stub DynamicDataSource used in SelectField
  //    for non-dynamic fields)
  // 2. every REQUIRED variable must be resolved — no leftover $context.* placeholder (missing
  //    context key) and no empty value — else the query is disabled
  // 3. OPTIONAL variables (e.g. actingTenantId) that are unresolved/empty are simply dropped
  //    from the request rather than blocking it (server falls back to the caller's tenant)
  const hasOperation = Boolean(dataSource.operation);
  const requestVars: Record<string, string> = {};
  let allRequiredResolved = hasOperation;
  for (const [key, val] of Object.entries(resolvedVars)) {
    const unresolved = isUnresolved(String(val));
    if (OPTIONAL_VARIABLES.has(key)) {
      if (!unresolved) requestVars[key] = val;
    } else if (unresolved) {
      allRequiredResolved = false;
    } else {
      requestVars[key] = val;
    }
  }

  const queryKey = ['dynamicOptions', dataSource.operation, requestVars] as const;

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const doc = getOperationDocument(dataSource.operation);
      const result = await graphqlClient.request<Record<string, unknown[]>>(
        doc,
        requestVars,
      );
      const rows = (result[dataSource.mapResponse.path] as Record<string, unknown>[] | undefined) ?? [];
      const { labelFields, labelSeparator = ' — ' } = dataSource.mapResponse;
      return rows.map((row): DynamicOption => ({
        value: String(row[dataSource.mapResponse.value] ?? ''),
        label: labelFields
          ? labelFields.map(f => String(row[f] ?? '')).filter(Boolean).join(labelSeparator)
          : String(row[dataSource.mapResponse.label] ?? ''),
      }));
    },
    enabled: Boolean(allRequiredResolved),
    staleTime: 5 * 60 * 1000, // 5 minutes — entity reference lists change infrequently
  });

  return {
    options: data ?? [],
    isLoading: isLoading && allRequiredResolved, // Don't report loading if disabled
    isError,
  };
}
